import { useNuxtApp, useRuntimeConfig, useRouter } from 'nuxt/app';
import { $fetch } from 'ofetch';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import type {
  Horario,
  IEstacion,
  IHorarios,
  ITarifas,
  Prettify,
} from '~~/interfaces';
import { useServiciosStore } from './servicios';
import { HorarioService } from '~~/helpers/horarios';
import { useUserStore } from './userconfig';
import {
  ALLOWED_SERVICES_PAIRS,
  BASE_SERVICIO_ID,
  ESTACION_BASE_BIOTREN_ID,
  ESTACIONES_BASE_SERVICIOS,
  type TServiciosSlug,
} from '~~/constants';

type Database<T extends object> = PouchDB.Database<T>;

interface HorarioAPI {
  salida?: string;
  llegada?: string;
  external?: boolean;
}

interface CambiarRutaParams {
  salida?: string;
  llegada?: string;
  cambiarRuta?: boolean;
}
type SimpleTarifa = Prettify<Pick<ITarifas, 'tarifas' | 'usuarios'>>;

export const useHorarioStore = defineStore('horarios', () => {
  const app = useNuxtApp();
  const config = useRuntimeConfig();
  const servicios = useServiciosStore();
  const user = useUserStore();
  const router = useRouter();
  const isOnline = useOnline();
  const $db = app.$db;

  // Initialize service
  const _horarioService = new HorarioService(
    $db?.Horarios as Database<IHorarios>,
    $db?.HorariosRemote as Database<IHorarios>,
    config.public.dbUrl || 'https://db.mibiotren.cl',
    'v4'
  );

  const firstRun = ref(true);

  const servicioSlug = computed(
    () => router.currentRoute.value.params.servicio as TServiciosSlug
  );
  const servicioActivo = computed(() => servicios.servicioActivo);
  const servicioId = computed(() => servicios.servicioId || BASE_SERVICIO_ID);

  const couldSync = ref(false);

  const salida = ref(
    servicios.estacionBaseSalidaLlegada?.salida?._id ||
      (servicioId.value === BASE_SERVICIO_ID ? ESTACION_BASE_BIOTREN_ID : '1')
  );

  const llegada = ref(
    servicios.estacionBaseSalidaLlegada?.llegada?._id ||
      ESTACIONES_BASE_SERVICIOS[servicioSlug.value]
  );

  watch(
    [servicioActivo, servicios],
    ([newServ], [oldServ]) => {
      if (!newServ) return;
      if (newServ?.srvid === oldServ?.srvid && salida.value && llegada.value) {
        return;
      }

      const userEstaciones = user.estaciones;
      const { salida: baseSalida, llegada: baseLlegada } =
        servicios.estacionBaseSalidaLlegada;

      const newSalida = userEstaciones.length
        ? userEstaciones[0]
        : baseSalida?._id;
      const newLlegada = userEstaciones.length
        ? userEstaciones[1] || baseLlegada?._id
        : baseLlegada?._id;

      if (newSalida && newSalida !== salida.value) {
        salida.value = newSalida;
      }
      if (newLlegada && newLlegada !== llegada.value) {
        llegada.value = newLlegada;
      }
    },
    { deep: true }
  );

  const salidaId = computed<string>({
    get: () => String(salida.value),
    set: (estSalida) => {
      salida.value = String(estSalida);
    },
  });
  const llegadaId = computed<string>({
    get: () => String(llegada.value),
    set: (estLlegada) => (llegada.value = String(estLlegada)),
  });

  const horariosServicio = ref<Record<string, Horario[]>>({
    [servicioId.value]: [],
  });
  const horarios = computed({
    get: () => horariosServicio.value[servicioId.value] || [],
    set: (newHorarios: Horario[]) => {
      if (!horariosServicio.value) {
        horariosServicio.value = {};
      }
      horariosServicio.value = Object.assign({}, horariosServicio.value, {
        [servicioId.value]: newHorarios,
      });
    },
  });
  const horariosVuelta = ref<Horario[]>([]);
  const loading = ref(false);
  const initLoad = ref(0);
  const isBuscandoHorario = ref(false);
  const tarifas = ref<Map<string, SimpleTarifa>>(new Map());

  // Initialize database sync
  const initializeSync = async () => {
    try {
      await _horarioService.syncDatabases(updateFn);
    } catch (e: unknown) {
      if (e instanceof Error) return;
      console.error('Database sync error:', e);
    } finally {
      couldSync.value = true;
      firstRun.value = false;
    }
  };

  async function getHorario({
    salida: sal,
    llegada: lle,
    external = false,
  }: HorarioAPI = {}) {
    if (isBuscandoHorario.value) return false;
    isBuscandoHorario.value = true;
    loading.value = true;
    let H: Horario[] = [];

    try {
      const estacionSalidaH = String(sal || salidaId.value);
      const estacionLlegadaH = String(lle || llegadaId.value);

      const existeSalida = servicios.estaciones.get(estacionSalidaH);
      const existeLlegada = servicios.estaciones.get(estacionLlegadaH);

      if (!existeSalida || !existeLlegada) {
        return Promise.reject(
          new Error(
            `Estaciones no encontradas para servicio ${servicioId} | salida: ` +
              estacionSalidaH +
              ', llegada: ' +
              estacionLlegadaH
          )
        );
      }

      H = await _horarioService.fetchHorario(
        servicioId.value,
        estacionSalidaH,
        estacionLlegadaH,
        isOnline && (external || !couldSync.value || firstRun.value)
      );

      horarios.value = H || [];

      if (H?.length) {
        const HVuelta = await _horarioService.fetchHorario(
          servicioId.value,
          estacionLlegadaH,
          estacionSalidaH,
          isOnline && (external || !couldSync.value || firstRun.value)
        );
        horariosVuelta.value = HVuelta || [];
      }
    } catch (error: unknown) {
      console.error('Error fetching horario:', error);
      horarios.value = [];
      horariosVuelta.value = [];
    } finally {
      if (sal) salidaId.value = String(sal);
      if (lle) llegadaId.value = String(lle);
      isBuscandoHorario.value = false;
      loading.value = false;
    }

    if (firstRun.value) {
      firstRun.value = false;
    }

    return H || [];
  }

  function updateFn(percent: number) {
    if (isNaN(parseInt(String(percent), 10))) return;
    initLoad.value = percent;
  }

  async function cambiarRuta({
    salida: salidaNew,
    llegada: llegadaNew,
    cambiarRuta = true,
  }: CambiarRutaParams = {}) {
    let sal = String(salidaNew || salidaId.value); // salida actual
    let lle = String(llegadaNew || llegadaId.value); // llegada actual

    const existeSalida = servicios.estaciones.get(sal);
    const existeLlegada = servicios.estaciones.get(lle);

    if (!existeSalida || !existeLlegada) {
      console.error('Estaciones no encontradas');
      return Promise.reject(new Error('Estaciones no encontradas'));
    }

    if (!llegadaNew && sal === llegadaId.value) {
      lle = salidaId.value;
    }
    if (!salidaNew && lle === salidaId.value) {
      sal = llegadaId.value;
    }

    // Then check for identical stations
    if (sal === lle) {
      lle =
        sal !== servicios.estacionBaseSalidaLlegada.salida._id
          ? servicios.estacionBaseSalidaLlegada.salida._id
          : servicios.estacionBaseSalidaLlegada.llegada._id;
    }

    salidaId.value = sal;
    llegadaId.value = lle;

    const estSalida = servicios.estaciones.get(salidaId.value);
    const estLlegada = servicios.estaciones.get(llegadaId.value);

    if (!estSalida || !estLlegada) {
      console.error('Estaciones no encontradas');
      return Promise.reject(new Error('Estaciones no encontradas'));
    }

    try {
      // gets horarios actuales
      await getHorario({
        salida: estSalida._id,
        llegada: estLlegada._id,
      });
    } catch (error) {
      console.error('Error fetching horarios:', error);
    } finally {
      if (cambiarRuta) {
        try {
          await router.replace({
            path: `/${servicios.servicioActivo?.slug}/${estSalida.slug}/${estLlegada.slug}`,
          });
        } catch (e) {
          console.error('Error routing', e);
        }
      }
    }
  }

  async function fetchTarifas(servicio: string) {
    if (tarifas.value.has(servicio)) {
      return;
    }

    const tarifasServicio = await $fetch<ITarifas>(
      `${config.public.dbUrl}/tarifas/${servicio}`
    )
      .then(
        (d) =>
          [d.tarifas, d.usuarios] as [
            Array<Array<Array<string> | null>>,
            string[],
          ]
      )
      .catch(() => null);

    if (tarifasServicio) {
      const tarifs = tarifasServicio[0];
      const usuars = tarifasServicio[1];

      if (!tarifs || !usuars) return;

      tarifas.value.set(servicio, {
        tarifas: tarifs,
        usuarios: usuars,
      });
    }
  }

  const tarifasHorario = computed<{ tarifas: string[]; usuarios: string[] }>(
    () => {
      const tarifasServicio = tarifas.value.get(
        ALLOWED_SERVICES_PAIRS.get(servicioSlug.value)!
      );

      if (!tarifasServicio || !estacionSalida.value || !estacionLlegada.value) {
        return {
          tarifas: [],
          usuarios: [],
        };
      }

      const estSalZona = parseInt(estacionSalida.value.zona, 10) - 1;
      const estLleZona = parseInt(estacionLlegada.value.zona, 10) - 1;

      return {
        tarifas: tarifasServicio?.tarifas[estSalZona]?.[estLleZona] || [],
        usuarios: tarifasServicio?.usuarios,
      };
    }
  );

  const estacionSalida = computed<IEstacion | undefined>(() => {
    return servicios.estaciones?.get(String(salida.value));
  });

  const estacionLlegada = computed<IEstacion | undefined>(() =>
    servicios.estaciones.get(String(llegada.value))
  );

  return {
    salida,
    llegada,
    horarios,
    tarifas: tarifasHorario,
    allTarifas: tarifas,
    loading,
    initLoad,
    horariosServicio,
    horariosVuelta,
    getHorario,
    updateFn,
    cambiarRuta,
    estacionSalida,
    estacionLlegada,
    isLoadingHorarios: computed(() => isBuscandoHorario.value),
    horariosVueltaMap: computed<Map<string, Horario>>(() =>
      horariosVuelta.value.reduce((init, horario) => {
        init.set(horario.S, horario);
        return init;
      }, new Map())
    ),
    servicioActivo,
    fetchTarifas,
    initializeSync,
    couldSyncHorarios: (could?: boolean) => (couldSync.value = could || false),
    couldSync,
    firstRun: computed(() => firstRun.value),
    completedFirstRun: () => {
      firstRun.value = false;
    },
  };
});

export type THorariosStore = typeof useHorarioStore;
